import { ErrorOutline } from '@mui/icons-material';
import { InputAdornment, TextField, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';

const nullableControlValue = value => (value === null || value === undefined ? '' : value);

// ImportTool.Field seed values; File Number is the same field the other import tools use
export const HHG_WEIGHT_IMPORT_FIELD_IDS = {
  FILE_NUMBER: 1,
  ESTIMATE_ID: 113,
  ACTUAL_LAND_WEIGHT: 114,
  ACTUAL_AIR_WEIGHT: 115,
  ACTUAL_SEA_WEIGHT: 116,
};

const createHhgWeightImportField = (fieldId, fieldName, fieldKey, dataType, isWeight = false) => ({
  fieldId,
  fieldName,
  fieldKey,
  dataType,
  isWeight,
  position: null,
  positionError: null,
});

// File Number and HHG Estimate ID are always required; the actual weights need at least one of them mapped. Weight field
// keys match the import result's properties.
export const HHG_WEIGHT_IMPORT_FIELDS = [
  createHhgWeightImportField(HHG_WEIGHT_IMPORT_FIELD_IDS.FILE_NUMBER, 'File Number', 'fileNumber', 'Text'),
  createHhgWeightImportField(HHG_WEIGHT_IMPORT_FIELD_IDS.ESTIMATE_ID, 'HHG Estimate ID', 'hhgEstimateId', 'Number'),
  createHhgWeightImportField(HHG_WEIGHT_IMPORT_FIELD_IDS.ACTUAL_LAND_WEIGHT, 'Actual Land Weight', 'actualLandWeight', 'Number', true),
  createHhgWeightImportField(HHG_WEIGHT_IMPORT_FIELD_IDS.ACTUAL_AIR_WEIGHT, 'Actual Air Weight', 'actualAirWeight', 'Number', true),
  createHhgWeightImportField(HHG_WEIGHT_IMPORT_FIELD_IDS.ACTUAL_SEA_WEIGHT, 'Actual Sea Weight', 'actualSeaWeight', 'Number', true),
];

export const HHG_WEIGHT_IMPORT_WEIGHT_FIELDS = HHG_WEIGHT_IMPORT_FIELDS.filter(field => field.isWeight);

const HHG_WEIGHT_IMPORT_FIELDS_BY_FIELD_ID = new Map(HHG_WEIGHT_IMPORT_FIELDS.map(field => [String(field.fieldId), field]));

export const getHhgWeightImportFieldByFieldId = fieldId => HHG_WEIGHT_IMPORT_FIELDS_BY_FIELD_ID.get(String(fieldId));

export const getInitialHhgWeightImportMappings = () => HHG_WEIGHT_IMPORT_FIELDS.reduce((mappings, field) => ({
  ...mappings,
  [field.fieldKey]: { ...field },
}), {});

const getRequiredLabel = field => (field.isWeight ? 'At least one weight class' : 'Yes');

export const getHhgWeightImportColumnMappings = props => {
  const { onPositionChange } = props;

  return [
    {
      accessorKey: 'fieldName',
      header: 'Field Name',
    },
    {
      accessorKey: 'position',
      header: 'Position',
      Cell: ({ row }) => (
        <TextField
          error={Boolean(row.original.positionError)}
          fullWidth
          size="small"
          value={nullableControlValue(row.original.position)}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          InputProps={{
            endAdornment: row.original.positionError ? (
              <InputAdornment position="end">
                <Tooltip title={row.original.positionError}>
                  <ErrorOutline color="error" fontSize="small" />
                </Tooltip>
              </InputAdornment>
            ) : null,
          }}
          onChange={event => onPositionChange(row.original.fieldKey, event.target.value)}
        />
      ),
    },
    {
      accessorKey: 'isWeight',
      header: 'Required',
      Cell: ({ row }) => getRequiredLabel(row.original),
    },
    {
      accessorKey: 'dataType',
      header: 'Data Type',
    },
  ];
};

getHhgWeightImportColumnMappings.propTypes = {
  onPositionChange: PropTypes.func.isRequired,
};

export const hhgWeightImportTemplateDisplayColumns = [
  {
    accessorKey: 'fieldName',
    header: 'Field Name',
    enableSorting: false,
  },
  {
    accessorKey: 'position',
    header: 'Position',
    enableSorting: false,
  },
  {
    accessorKey: 'isWeight',
    header: 'Required',
    enableSorting: false,
    Cell: ({ row }) => getRequiredLabel(row.original),
  },
  {
    accessorKey: 'dataType',
    header: 'Data Type',
    enableSorting: false,
  },
];
